// ─────────────────────────────────────────────────────────────────
// Glass-i — HR Departments page (Stage HRMS-1).
// COMPACT INLINE CRUD — same pattern as Glass Category Master. Every
// cell edits live; dirty rows highlight; Save All commits via
// /bulk-save in one round-trip. RBAC server-side (hrms.department.*).
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Input, Switch, Space, Spin, Table, Tooltip, message } from 'antd';
import {
  PlusOutlined, SaveOutlined, ReloadOutlined, ApartmentOutlined, SearchOutlined
} from '@ant-design/icons';

import { hrDepartmentsApi } from './hrDepartmentsApi';
import {
  normalizeDepartmentRows, sortDepartments, validateDepartmentRows,
  buildDepartmentBulkPayload, markDirtyRow
} from './hrDepartmentsHelpers';
import '../hrms.css';

export default function DepartmentsPage() {
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving]   = useState(false);
  const [search, setSearch]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const out = await hrDepartmentsApi.list({ limit: 1000 });
      setRows(sortDepartments(normalizeDepartmentRows((out && out.rows) || [])));
    } catch (e) {
      message.error(e?.response?.data?.error?.message || e.message || 'Failed to load departments.');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const visible = useMemo(() => {
    const needle = (search || '').trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((r) => [r.departmentCode, r.departmentName, r.description]
      .some((v) => v && String(v).toLowerCase().includes(needle)));
  }, [rows, search]);

  const dirtyCount = useMemo(() => rows.filter((r) => r._dirty).length, [rows]);
  const patch = (clientRowId, p) => setRows((prev) => markDirtyRow(prev, clientRowId, p));

  const addRow = () => {
    setRows((prev) => [...prev, {
      _clientRowId: `hd-new-${Date.now()}-${prev.length + 1}`, _dirty: true,
      id: null, departmentCode: '', departmentName: '', description: '', active: true
    }]);
  };

  const saveAll = async () => {
    const dirty = rows.filter((r) => r._dirty);
    if (dirty.length === 0) { message.info('Nothing to save.'); return; }
    const { ok, errors } = validateDepartmentRows(rows);
    if (!ok) { message.error(errors[0].message); return; }
    setSaving(true);
    try {
      const out = await hrDepartmentsApi.bulkSave(buildDepartmentBulkPayload(rows, []));
      message.success(`Saved ${out.inserted || 0} new · updated ${out.updated || 0}.`);
      load();
    } catch (e) {
      message.error(e?.response?.data?.error?.message || 'Failed to save departments.');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    { title: 'Department Code', dataIndex: 'departmentCode', width: 200,
      render: (v, row) => (
        <Input size="small" variant="borderless" value={v} placeholder="e.g., SALES"
          maxLength={40} onChange={(e) => patch(row._clientRowId, { departmentCode: e.target.value })} />
      ) },
    { title: 'Department Name', dataIndex: 'departmentName', width: 280,
      render: (v, row) => (
        <Input size="small" variant="borderless" value={v} placeholder="e.g., Sales"
          maxLength={160} onChange={(e) => patch(row._clientRowId, { departmentName: e.target.value })} />
      ) },
    { title: 'Description', dataIndex: 'description',
      render: (v, row) => (
        <Input size="small" variant="borderless" value={v} placeholder="Optional notes"
          maxLength={2000} onChange={(e) => patch(row._clientRowId, { description: e.target.value })} />
      ) },
    { title: 'Active', dataIndex: 'active', width: 90, align: 'center',
      render: (v, row) => (
        <Switch size="small" checked={v !== false}
          onChange={(checked) => patch(row._clientRowId, { active: checked })} />
      ) }
  ];

  return (
    <div className="hr-shell">
      <div className="hr-headstrip">
        <div className="hr-headstrip__lead">
          <span className="hr-headstrip__icon"><ApartmentOutlined /></span>
          <div className="hr-headstrip__text">
            <span className="hr-headstrip__title">Departments</span>
            <span className="hr-headstrip__subtitle">
              Edit any cell directly — click Save All Changes to persist.
            </span>
          </div>
        </div>
        <Space size={6}>
          <span className="hr-stat-chip"><strong>{rows.length}</strong> total</span>
          {dirtyCount > 0 && (
            <span className="hr-stat-chip hr-stat-chip--dirty"><strong>{dirtyCount}</strong> unsaved</span>
          )}
        </Space>
      </div>

      <div className="hr-toolbar">
        <Input allowClear size="small"
          prefix={<SearchOutlined style={{ color: '#94a3b8' }} />}
          placeholder="Search code, name, description…"
          value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 320 }} />
        <div className="hr-toolbar__spacer" />
        <Tooltip title="Refresh"><Button size="small" icon={<ReloadOutlined />} onClick={load} /></Tooltip>
        <Button size="small" icon={<PlusOutlined />} onClick={addRow}>Add Row</Button>
        <Button size="small" type="primary" icon={<SaveOutlined />} onClick={saveAll}
          loading={saving} disabled={saving || dirtyCount === 0}>Save All Changes</Button>
      </div>

      <div className="hr-card">
        <Spin spinning={loading}>
          <Table className="hr-grid" size="small" rowKey="_clientRowId"
            dataSource={visible} columns={columns} pagination={false} sticky
            scroll={{ x: 'max-content' }}
            rowClassName={(r) => r._dirty ? 'hr-dirty-row' : ''}
            locale={{ emptyText: rows.length === 0
              ? 'No departments yet. Click Add Row to start.'
              : 'No departments match the current search.' }} />
        </Spin>
      </div>
    </div>
  );
}
